/**
 * Pure helpers for the config click handler.
 *
 * Split from `ConfigClickHandler.tsx` so the JSX file only exports the
 * React component — Vite's `react-refresh` plugin rejects mixed
 * component/value exports in a single module, and keeping these
 * utilities beside the component also lets unit tests import them
 * without spinning up a React render tree.
 */

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * HTML attribute written by the `withComponentRules` HOC (Phase 6) on
 * the wrapper div around each registered component. The capture
 * listener walks the DOM from `event.target` upward until it finds an
 * ancestor carrying this attribute — the attribute value is the
 * component id used to look the registration up.
 *
 * Exported so tests can construct fixtures with the real attribute
 * name, and to keep this string spelled in exactly one place.
 */
export const COMPONENT_ID_ATTRIBUTE = 'data-component-id';

// ---------------------------------------------------------------------------
// URL helpers
// ---------------------------------------------------------------------------

/**
 * Build the path for a component's admin config page.
 *
 * Kept as a named helper so routing conventions live in one place — if
 * the URL structure ever changes, only this function needs to update.
 *
 * @param componentId - The registered component id (e.g.,
 *   `"claimflow.openCoverageCard"`). Encoded so ids containing
 *   reserved URL characters still produce a valid path segment.
 */
export function buildConfigPath(componentId: string): string {
  return `/admin/components/${encodeURIComponent(componentId)}/config`;
}

// ---------------------------------------------------------------------------
// DOM helpers
// ---------------------------------------------------------------------------

/**
 * Walk up from `start` looking for the nearest ancestor that carries
 * the `data-component-id` attribute. Returns the found element, or
 * `null` if the walk reaches the document root without a match.
 *
 * Uses a `while` loop (rather than `Element.closest()`) because the
 * event target may be a `Text` node or other non-`Element` — the DOM
 * API requires us to hop to the parent before `closest()` is valid,
 * and at that point a loop is simpler than branching.
 *
 * @param start - The event target from a DOM event. May be `null` (for
 *   synthetic events without a target) or a non-`Element` node (text,
 *   attribute) — the walk normalises both cases.
 */
export function findComponentAncestor(
  start: EventTarget | null,
): Element | null {
  let node: Node | null = start instanceof Node ? start : null;

  // Walk up from a text node / attribute target to the nearest element.
  while (node !== null && !(node instanceof Element)) {
    node = node.parentNode;
  }

  let element: Element | null = node;
  while (element !== null) {
    if (element.hasAttribute(COMPONENT_ID_ATTRIBUTE)) {
      return element;
    }
    element = element.parentElement;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Keyboard helpers
// ---------------------------------------------------------------------------

/**
 * Returns `true` when the keyboard event matches `CMD`/`Ctrl`+`Shift`+`E`.
 *
 * Accepts both uppercase `'E'` and lowercase `'e'` — on some platforms
 * the OS reports the event's `key` as uppercase when Shift is held and
 * on others it stays lowercase, so we match both to be safe.
 *
 * This shortcut is intentionally a modifier-heavy combination to avoid
 * collision with common in-document shortcuts (e.g., a search field's
 * own `E` key). `CMD+SHIFT+E` is currently used by some browser dev
 * tools and IDEs — swapping to a different combo is a Phase-4 follow-up
 * (see the plan's Risk table).
 */
export function isToggleShortcut(event: KeyboardEvent): boolean {
  const hasModifier = event.metaKey || event.ctrlKey;
  const hasShift = event.shiftKey;
  const isE = event.key === 'E' || event.key === 'e';
  return hasModifier && hasShift && isE;
}
